/**
 * WorkQueueCard — Lead card for the work queue.
 *
 * Shows business name + location, "why now" signal, confidence badge,
 * and one primary action button. No drag — click to preview, button to act.
 */
import React from 'react';
import { Zap, Star, Loader2, ArrowRight, MapPin, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getConfidence, getNextAction, formatRelativeTime } from '@/components/leads/mockData';
import {
  estimateDealValue,
  computeDealScore, getNextBestAction, getStaleDays,
} from '@/components/deal/mockDealData';

const CONFIDENCE_LABELS = {
  high: 'High match',
  medium: 'Good match',
  low: 'Needs data',
};

const CONFIDENCE_TOOLTIPS = {
  high: 'Strong fit based on category, location, and your past wins.',
  medium: 'Reasonable fit. Worth a pitch.',
  low: "Not enough info yet — we'll suggest a fit once we know more.",
};

function WorkQueueCard({ lead, onSelect, onPrimaryAction, businesses, creatives, proposals }) {
  const isBackendLead = !!lead.fromBackend;
  const confidence = getConfidence(lead);
  const nextAction = getNextAction(lead);
  const isEnriching = nextAction.type === 'enriching';

  // Phase 12: intelligence
  const dealScore = isBackendLead
    ? {
      priorityKey: lead.needsAttention ? 'high' : lead.stale ? 'medium' : 'low',
      priorityLabel: lead.needsAttention ? 'Act soon' : lead.stale ? 'Needs follow-up' : 'Monitor',
      priorityClassName: lead.needsAttention
        ? 'border-coral/20 bg-coral/5 text-coral'
        : lead.stale
          ? 'border-amber-200 bg-amber-50 text-amber-700'
          : 'border-slate-200 bg-slate-50 text-slate-600',
    }
    : computeDealScore(lead, businesses, creatives, proposals);
  const nba = isBackendLead
    ? {
      action: lead.recommendedNextStepCode,
      label: lead.nextActionLabel || lead.recommendedNextStepText || 'Open workspace',
      description: lead.nextActionDescription || lead.recommendedNextStepText || 'Continue this workspace.',
    }
    : getNextBestAction(lead, businesses, creatives, proposals);
  const staleInfo = isBackendLead
    ? {
      isStale: lead.stale,
      days: lead.lastActivityAt
        ? Math.max(0, Math.round((Date.now() - new Date(lead.lastActivityAt).getTime()) / (1000 * 60 * 60 * 24)))
        : 0,
    }
    : getStaleDays(lead, businesses, creatives, proposals);
  const summaryValue = isBackendLead
    ? (lead.queueStatusLabel || 'Open workspace')
    : estimateDealValue(lead);

  // Use NBA label for the button when resuming an existing deal
  const buttonLabel = nba.action !== 'add_business' && nba.action !== 'wait' && nba.action !== 'monitor'
    ? nba.label
    : nextAction.label;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(lead)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(lead); } }}
      aria-label={`${lead.businessName} — ${dealScore.priorityLabel}, ${CONFIDENCE_LABELS[confidence]}`}
      className={`group max-w-full overflow-hidden rounded-2xl border-l-[3px] border border-slate-200 bg-card p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg cursor-pointer ${
        dealScore.priorityKey === 'high' ? 'border-l-coral' : dealScore.priorityKey === 'medium' ? 'border-l-amber-400' : 'border-l-slate-300'
      } ${staleInfo.isStale ? 'bg-amber-50/30' : ''}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <Badge variant="outline" className={`rounded-full border text-[11px] ${dealScore.priorityClassName}`}>
            {dealScore.priorityLabel}
          </Badge>

          <h3 className="mt-3 text-base font-semibold text-slate-950 transition-colors group-hover:text-slate-700">
            {lead.businessName}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
            <span>{lead.businessCategory}</span>
            {lead.businessRating != null && (
              <span className="flex items-center gap-1 text-amber-600">
                <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
                {lead.businessRating}
              </span>
            )}
            <span className="text-slate-400" title={CONFIDENCE_TOOLTIPS[confidence]}>{CONFIDENCE_LABELS[confidence]}</span>
          </div>
        </div>

        <Button
          size="sm"
          variant={isEnriching ? 'outline' : 'default'}
          disabled={isEnriching}
          onClick={(e) => {
            e.stopPropagation();
            if (!isEnriching) onPrimaryAction(lead);
          }}
          title={typeof buttonLabel === 'string' ? buttonLabel : undefined}
          // Cap width + clamp to 2 lines so long backend-provided labels (e.g.
          // "Add the latest report snapshot for this live campaign.") don't
          // expand the button and squeeze the business-info column.
          className={`${isEnriching ? '' : 'bg-slate-950 text-white hover:bg-slate-800'} w-full self-start rounded-xl whitespace-normal h-auto py-2 text-left sm:w-auto sm:max-w-[14rem]`}
        >
          {isEnriching ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              <span className="hidden sm:inline">Enriching</span>
            </>
          ) : (
            <>
              <span className="line-clamp-2 leading-snug">{buttonLabel}</span>
              <ArrowRight className="h-3 w-3 ml-0.5 shrink-0" />
            </>
          )}
        </Button>
      </div>

      <div className="mt-4">
        <p className="text-[15px] font-semibold leading-snug text-slate-900 line-clamp-2">{lead.triggerEvent}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <Zap className="h-3 w-3 text-coral" />
          {formatRelativeTime(lead.triggerDate)}
          {lead.businessAddress && (
            <>
              <span className="text-slate-300">·</span>
              <span className="min-w-0 truncate">{lead.businessAddress}</span>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
            {lead.status !== 'closed' && (
          <span className="font-semibold text-slate-900">{summaryValue}</span>
        )}
        <span className="min-w-0 flex-1 truncate text-slate-500" title={nba.label}>{nba.label}</span>
        {staleInfo.isStale && (
          <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-amber-600">
            <AlertTriangle className="h-3 w-3" />
            {staleInfo.days}d stale
          </span>
        )}
      </div>
    </div>
  );
}

export default React.memo(WorkQueueCard);
